import { useEffect, useMemo, useState } from "react";
import { useLivestreamChat } from "../../../context/LivestreamChatProvider";
import { useLivestreamPopout } from "../../../context/LivestreamPopoutProvider";
import { useLocalization } from "../../../hooks/useLocalization";
import { useTwitchStream } from "../../../hooks/useTwitchStream";
import {
  buildTwitchChannelUrl,
  buildTwitchChatEmbedSrc,
  buildTwitchEmbedSrc,
  getTwitchEmbedParents,
  parseTwitchPlayerEmbedChannel,
} from "../../../services/twitchServices";
import type { LiveStreamEntry } from "./useHomeStream";

const DEFAULT_CHANNEL = "finnhouse";

interface UseLivestreamSectionArgs {
  matchSoon: boolean;
  enabled: boolean;
  streamChannel?: string | null;
  streamLink?: string;
  liveStreamEntries?: LiveStreamEntry[];
  hasLiveTaggedMatches?: boolean;
  /** True while match/stream data that affects visibility is still loading */
  visibilityPending?: boolean;
}

export const useLivestreamSection = ({
  matchSoon,
  enabled,
  streamChannel,
  streamLink,
  liveStreamEntries = [],
  hasLiveTaggedMatches = false,
  visibilityPending = false,
}: UseLivestreamSectionArgs) => {
  const { strings, locale } = useLocalization();
  const { isPoppedOut, popoutSupported, openPopout, openPopouts, closePopout, syncPopout } =
    useLivestreamPopout();
  const { chatOpen, toggleChat, setChatConfig } = useLivestreamChat();
  const [expanded, setExpanded] = useState(false);
  const [hiddenDualChats, setHiddenDualChats] = useState<Record<string, boolean>>({});

  const embedParents = useMemo(() => getTwitchEmbedParents(), []);
  const embedEntries = useMemo(() => {
    const channelSet = new Set<string>();
    const channels = liveStreamEntries.map((entry) => entry.channel).filter(Boolean);
    if (streamChannel) {
      channels.unshift(streamChannel);
    }

    return channels
      .filter((channel) => {
        const key = channel.toLowerCase();
        if (channelSet.has(key)) return false;
        channelSet.add(key);
        return true;
      })
      .slice(0, 2)
      .map((channel) => ({
        channel,
        src: buildTwitchEmbedSrc(channel, embedParents),
        chatSrc: buildTwitchChatEmbedSrc(channel, embedParents),
        link:
          liveStreamEntries.find((entry) => entry.channel.toLowerCase() === channel.toLowerCase())
            ?.link ?? buildTwitchChannelUrl(channel),
      }));
  }, [embedParents, liveStreamEntries, streamChannel]);

  const resolvedStatusChannel = embedEntries.length > 0 ? embedEntries[0].channel : streamChannel;
  const resolvedStatusLink = embedEntries.length > 0 ? embedEntries[0].link : streamLink;
  const dualPrimaryChannel = embedEntries.length > 0 ? embedEntries[0].channel : null;
  const dualSecondaryChannel = embedEntries.length > 1 ? embedEntries[1].channel : null;

  // Only healthcheck Twitch from 1h before kickoff (or while matches are LIVE-tagged).
  const inStreamWindow = matchSoon || hasLiveTaggedMatches;
  const twitchCheckEnabled = enabled && inStreamWindow;

  const { stream, loading } = useTwitchStream(twitchCheckEnabled, resolvedStatusChannel);
  const { stream: dualPrimaryStream } = useTwitchStream(
    twitchCheckEnabled && embedEntries.length > 1,
    dualPrimaryChannel,
  );
  const { stream: dualSecondaryStream } = useTwitchStream(
    twitchCheckEnabled && embedEntries.length > 1,
    dualSecondaryChannel,
  );

  const dualViewerByChannel = useMemo(() => {
    const entries = [dualPrimaryStream, dualSecondaryStream].filter(Boolean);
    const counts = new Map<string, number>();
    entries.forEach((entry) => {
      if (!entry?.login || entry.viewerCount == null) {
        return;
      }
      counts.set(entry.login.toLowerCase(), entry.viewerCount);
    });
    return counts;
  }, [dualPrimaryStream, dualSecondaryStream]);

  const channel = stream?.login || resolvedStatusChannel || DEFAULT_CHANNEL;
  const isLive = stream?.live === true;
  // Show player only when Helix healthcheck confirms live inside the match window.
  const shouldShow = twitchCheckEnabled && isLive;
  const streamStatusPending = twitchCheckEnabled && (loading || visibilityPending);

  const embedSrc = useMemo(
    () => buildTwitchEmbedSrc(channel, embedParents),
    [channel, embedParents],
  );
  const chatSrc = useMemo(
    () => buildTwitchChatEmbedSrc(channel, embedParents),
    [channel, embedParents],
  );
  const channelUrl = useMemo(
    () => resolvedStatusLink?.trim() || buildTwitchChannelUrl(channel),
    [resolvedStatusLink, channel],
  );
  const popoutTitle = isLive ? strings.LIVE.LIVE_NOW_TITLE : strings.LIVE.TITLE;

  const hasMultipleEmbeds = embedEntries.length > 1;
  const renderedEmbedEntries = useMemo(
    () =>
      embedEntries.length
        ? embedEntries
        : [{ channel: "finnhouse", src: embedSrc, link: buildTwitchChannelUrl("finnhouse") }],
    [embedEntries, embedSrc],
  );
  const showDualLayout = hasMultipleEmbeds && !isPoppedOut;
  const primaryRenderedEntry = renderedEmbedEntries[0];
  const primaryRenderedChannel = parseTwitchPlayerEmbedChannel(primaryRenderedEntry.src);
  const primaryStreamLink =
    primaryRenderedEntry.link ||
    buildTwitchChannelUrl(primaryRenderedChannel || primaryRenderedEntry.channel);
  const primaryStreamerLabel = primaryRenderedChannel || primaryRenderedEntry.channel;

  useEffect(() => {
    if (!shouldShow) {
      setChatConfig(null);
      return;
    }

    setChatConfig({ src: chatSrc, title: strings.LIVE.CHAT_TITLE });
  }, [shouldShow, chatSrc, strings.LIVE.CHAT_TITLE, setChatConfig]);

  useEffect(() => {
    if (!expanded) {
      return;
    }

    closePopout();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExpanded(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [expanded, closePopout]);

  useEffect(() => {
    if (!isPoppedOut) {
      return;
    }
    syncPopout({ embedSrc, title: popoutTitle });
  }, [isPoppedOut, channel, popoutTitle, syncPopout, embedSrc]);

  const viewerLabel =
    isLive && stream.viewerCount != null
      ? strings.LIVE.VIEWERS.replace("{count}", stream.viewerCount.toLocaleString(locale))
      : null;

  const showHeader = !expanded && shouldShow;
  const showLoadingHint = loading && stream == null;

  const toggleExpanded = () => {
    setExpanded((value) => !value);
  };

  const togglePopout = () => {
    if (isPoppedOut) {
      closePopout();
      return;
    }
    openPopout({ embedSrc, chatSrc, title: popoutTitle });
  };

  const openDualPopout = () => {
    openPopouts(
      embedEntries.map((entry) => ({
        embedSrc: entry.src,
        chatSrc: entry.chatSrc,
        title: `${strings.LIVE.LIVE_NOW_TITLE} - ${entry.channel}`,
      })),
    );
  };

  const isDualChatHidden = (entryChannel: string) =>
    Boolean(hiddenDualChats[entryChannel.toLowerCase()]);

  const toggleDualChat = (entryChannel: string) => {
    const key = entryChannel.toLowerCase();
    setHiddenDualChats((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const dualViewerLabel = (entryChannel: string) => {
    const count = dualViewerByChannel.get(entryChannel.toLowerCase());
    return count != null ? strings.LIVE.VIEWERS.replace("{count}", count.toLocaleString()) : null;
  };

  return {
    strings,
    shouldShow,
    isLive,
    expanded,
    chatOpen,
    isPoppedOut,
    popoutSupported,
    embedSrc,
    channelUrl,
    viewerLabel,
    showHeader,
    showLoadingHint,
    streamStatusPending,
    title: isLive ? strings.LIVE.LIVE_NOW_TITLE : strings.LIVE.TITLE,
    toggleChat,
    toggleExpanded,
    togglePopout,
    closePopout,
    hasMultipleEmbeds,
    embedEntries,
    renderedEmbedEntries,
    showDualLayout,
    primaryStreamLink,
    primaryStreamerLabel,
    openDualPopout,
    isDualChatHidden,
    toggleDualChat,
    dualViewerLabel,
  };
};
